import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

const VERT = /* glsl */ `
  attribute float aSpeed;
  attribute float aPhase;
  attribute float aSize;
  uniform float uTime;
  uniform float uScroll;

  void main() {
    vec3 pos = position;
    float speed = aSpeed * (1.0 + uScroll * 2.0);
    pos.y = mod(pos.y + uTime * speed, 40.0) - 20.0;
    pos.x += sin(uTime * 0.4 + aPhase) * 0.4;
    pos.z += cos(uTime * 0.3 + aPhase * 1.3) * 0.3;

    vec4 mvPos = modelViewMatrix * vec4(pos, 1.0);
    gl_PointSize = aSize * (30.0 / -mvPos.z);
    gl_Position = projectionMatrix * mvPos;
  }
`

const FRAG = /* glsl */ `
  uniform float uTime;
  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    if (d > 0.5) discard;
    float alpha = (1.0 - d * 2.0) * 0.4;
    // alternate gold / purple per fragment brightness
    vec3 col = mix(vec3(0.79, 0.66, 0.30), vec3(0.49, 0.23, 0.93), step(0.5, fract(gl_FragCoord.x * 0.01)));
    gl_FragColor = vec4(col, alpha);
  }
`

export default function AmbientParticles({ scrollRef }: { scrollRef: React.MutableRefObject<number> }) {
  const matRef = useRef<THREE.ShaderMaterial>(null)

  const [positions, speeds, phases, sizes] = useMemo(() => {
    const N = 2000
    const pos = new Float32Array(N * 3)
    const spd = new Float32Array(N)
    const phs = new Float32Array(N)
    const sz = new Float32Array(N)
    for (let i = 0; i < N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 60
      pos[i * 3 + 1] = (Math.random() - 0.5) * 40
      pos[i * 3 + 2] = (Math.random() - 0.5) * 30 - 5
      spd[i] = 0.05 + Math.random() * 0.15
      phs[i] = Math.random() * Math.PI * 2
      sz[i] = 1 + Math.random() * 2
    }
    return [pos, spd, phs, sz]
  }, [])

  const uniforms = useMemo(
    () => ({ uTime: { value: 0 }, uScroll: { value: 0 } }),
    []
  )

  useFrame(({ clock }) => {
    if (!matRef.current) return
    matRef.current.uniforms.uTime.value = clock.getElapsedTime()
    matRef.current.uniforms.uScroll.value = scrollRef.current
  })

  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-aSpeed" args={[speeds, 1]} />
        <bufferAttribute attach="attributes-aPhase" args={[phases, 1]} />
        <bufferAttribute attach="attributes-aSize" args={[sizes, 1]} />
      </bufferGeometry>
      <shaderMaterial
        ref={matRef}
        vertexShader={VERT}
        fragmentShader={FRAG}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  )
}
